import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Moon, Globe, Shield, HelpCircle, ExternalLink, Link2, ChevronRight, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';

interface SettingToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon: React.ReactNode;
}

function SettingToggle({ label, description, checked, onChange, icon }: SettingToggleProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          checked ? 'bg-primary-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}

interface SettingLinkProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  href: string;
}

function SettingLink({ label, description, icon, href }: SettingLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-4 px-4 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <ExternalLink className="w-4 h-4 text-gray-400" />
    </a>
  );
}

interface SettingNumberInputProps {
  label: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
  icon: React.ReactNode;
  min?: number;
  max?: number;
}

function SettingNumberInput({ label, description, value, onChange, icon, min = 1, max = 20 }: SettingNumberInputProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          disabled={value <= min}
        >
          -
        </button>
        <span className="w-8 text-center font-medium text-gray-900">{value}</span>
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          disabled={value >= max}
        >
          +
        </button>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [focusLimit, setFocusLimit] = useState(() => {
    const stored = localStorage.getItem('lifeguide_focus_limit');
    return stored ? parseInt(stored, 10) : 5;
  });

  // Persist focus limit to localStorage
  useEffect(() => {
    localStorage.setItem('lifeguide_focus_limit', String(focusLimit));
  }, [focusLimit]);

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

      {/* Integrations */}
      <Card className="mb-6">
        <CardContent>
          <Link
            to="/integrations"
            className="flex items-center justify-between py-2 -mx-2 px-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-coral-100 rounded-lg">
                <Link2 className="w-5 h-5 text-coral-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Integrations</p>
                <p className="text-sm text-gray-500">Connect Asana, calendars, and more</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </Link>
        </CardContent>
      </Card>

      {/* Focus Settings */}
      <Card className="mb-6">
        <CardContent>
          <h3 className="font-medium text-gray-900 mb-2">Focus</h3>
          <div className="divide-y divide-gray-100">
            <SettingNumberInput
              label="Max Focus Tasks"
              description="Soft limit for focus tasks (shows warning when exceeded)"
              value={focusLimit}
              onChange={setFocusLimit}
              icon={<Target className="w-5 h-5 text-primary-600" />}
              min={1}
              max={10}
            />
          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card className="mb-6">
        <CardContent>
          <h3 className="font-medium text-gray-900 mb-2">Preferences</h3>
          <div className="divide-y divide-gray-100">
            <SettingToggle
              label="Notifications"
              description="Receive push notifications for new conversations"
              checked={notifications}
              onChange={setNotifications}
              icon={<Bell className="w-5 h-5 text-gray-600" />}
            />
            <SettingToggle
              label="Dark Mode"
              description="Use dark theme throughout the app"
              checked={darkMode}
              onChange={setDarkMode}
              icon={<Moon className="w-5 h-5 text-gray-600" />}
            />
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card className="mb-6">
        <CardContent>
          <h3 className="font-medium text-gray-900 mb-2">About</h3>
          <div className="divide-y divide-gray-100">
            <SettingLink
              label="Privacy Policy"
              description="How we handle your data"
              icon={<Shield className="w-5 h-5 text-gray-600" />}
              href="https://lifeguide.app/privacy"
            />
            <SettingLink
              label="Help & Support"
              description="Get help using LifeGuide"
              icon={<HelpCircle className="w-5 h-5 text-gray-600" />}
              href="https://lifeguide.app/help"
            />
            <SettingLink
              label="Language"
              description="English (US)"
              icon={<Globe className="w-5 h-5 text-gray-600" />}
              href="#"
            />
          </div>
        </CardContent>
      </Card>

      {/* Version */}
      <p className="text-center text-sm text-gray-400">
        LifeGuide v1.1.0
      </p>
    </div>
  );
}
